import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Button,
  FormControl,
  FormLabel,
  Select,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useEffect, useState, useContext } from "react";
import { AppContext } from "contexts/AppContext";
import InputField from "views/admin/events/components/InputField";

export default function AddReservationModal({
  isOpen,
  onClose,
  onCreateReservationItem,
}) {
  const [itemDate, setItemDate] = useState("");
  const [blockType, setBlockType] = useState("full_day");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");

  const [itemDateError, setItemDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const textColor = useColorModeValue("navy.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const labelAccent = useColorModeValue("brand.500", "brand.400");

  const { fetchReservationData } = useContext(AppContext);

  useEffect(() => {
    setItemDate("");
    setBlockType("full_day");
    setStartTime("");
    setEndTime("");
    setReason("");
    setItemDateError("");
    setTimeError("");
    setSaveError("");
    setIsSaving(false);
  }, [isOpen]);

  const handleSave = async () => {
    let isValid = true;

    if (!itemDate) {
      setItemDateError("Date is required.");
      isValid = false;
    } else {
      setItemDateError("");
    }

    if (blockType === "time_range") {
      if (!startTime || !endTime) {
        setTimeError("Start and end time are required for a time block.");
        isValid = false;
      } else if (endTime === startTime) {
        setTimeError("End time must be different from start time.");
        isValid = false;
      } else {
        setTimeError("");
      }
    } else {
      setTimeError("");
    }

    if (!isValid) {
      return;
    }

    setIsSaving(true);
    setSaveError("");
    try {
      await onCreateReservationItem({
        reservationDate: itemDate,
        blockType,
        startTime: blockType === "time_range" ? startTime : "",
        endTime: blockType === "time_range" ? endTime : "",
        reason: reason.trim(),
      });
      await fetchReservationData();
      onClose();
    } catch (error) {
      setSaveError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save reservation block."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader flex={1} textAlign="center">
          Add Reservation Block
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InputField
            label="Date"
            type="date"
            value={itemDate}
            onChange={event => setItemDate(event.target.value)}
            errorMessage={itemDateError}
            isRequired={true}
            color={textColor}
          />

          <FormControl mt="16px">
            <FormLabel ms="4px" fontSize="sm" fontWeight="500" color={textColor}>
              Block Type <Text as="span" color={labelAccent}>*</Text>
            </FormLabel>
            <Select
              fontSize="sm"
              size="lg"
              variant="auth"
              value={blockType}
              onChange={event => setBlockType(event.target.value)}
              borderColor={borderColor}
              mb="24px"
            >
              <option value="full_day">Full Day</option>
              <option value="time_range">Time Range</option>
            </Select>
          </FormControl>

          {blockType === "time_range" ? (
            <>
              <InputField
                label="Start Time"
                type="time"
                value={startTime}
                onChange={event => setStartTime(event.target.value)}
                errorMessage={timeError}
                isRequired={true}
                color={textColor}
                mt="16px"
              />
              <InputField
                label="End Time"
                type="time"
                value={endTime}
                onChange={event => setEndTime(event.target.value)}
                errorMessage={timeError}
                isRequired={true}
                color={textColor}
                mt="16px"
              />
            </>
          ) : null}

          <FormControl mt="16px">
            <FormLabel ms="4px" fontSize="sm" fontWeight="500" color={textColor}>
              Reason (optional)
            </FormLabel>
            <Textarea
              value={reason}
              onChange={event => setReason(event.target.value)}
              fontSize="sm"
              size="md"
              borderColor={borderColor}
              placeholder="Private event, maintenance, kitchen pause, etc."
              mb="24px"
            />
          </FormControl>

          <Button
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%"
            h="50px"
            mt="40px"
            mb="30px"
            onClick={handleSave}
            isDisabled={!itemDate || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
          {saveError ? (
            <Text color="red.500" mb="20px" fontSize="sm" fontWeight="500">
              {saveError}
            </Text>
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
